import { useEffect, useMemo, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { SortingState } from '@tanstack/react-table';
import { MdAdd } from 'react-icons/md';
import { useForm } from 'react-hook-form';
import { Outlet, useNavigate } from 'react-router-dom';

import Button from 'components/Common/Button';
import DataTable from 'components/Common/DataTable';
import DateRangePicker from 'components/Common/DateRangePicker';
import Select from 'components/Common/Select';
import Pagination, { usePagination } from '@/components/Common/Pagination';
import { useDeleteWebhook, useGetWebhookLogs, useGetWebhooks } from '@/apis/agency/webhooks/hook';
import { useGetAgencyOrganizations } from '@/apis/agency/organizations/hook';
import * as urls from 'routes/config';
import { getWebhookColumns } from './utils/columns';
import { getWebhookLogColumns } from './utils/logColumns';

const WEBHOOKS_BASE = `/agency/${urls.agency.developers}/${urls.developers.webhooks}`;

const STATUS_FILTER_OPTIONS = [
  { value: ' ', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' }
];

interface LogsFilterForm {
  dateRange: string;
}

interface WebhooksFilterForm {
  status: string;
}

const sectionHeaderSx = {
  fontSize: { xs: '1rem', lg: 18 },
  fontWeight: 500
} as const;

export default function Webhooks() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);
  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination();
  const { data: agencyOrganization } = useGetAgencyOrganizations();

  const { control, watch } = useForm<LogsFilterForm>({
    defaultValues: { dateRange: '' }
  });
  const dateRange = watch('dateRange');

  const { control: webhooksFilterControl, watch: watchWebhooksFilter } = useForm<WebhooksFilterForm>({
    defaultValues: { status: ' ' }
  });
  const statusFilter = watchWebhooksFilter('status');

  useEffect(() => {
    if (tab === 1) resetPage();
  }, [dateRange, tab, resetPage]);

  useEffect(() => {
    if (tab === 0) resetPage();
  }, [statusFilter, tab, resetPage]);

  const logParams = useMemo(() => {
    const params: Record<string, unknown> = {
      page: currentPage,
      per_page: perPage
    };
    if (dateRange) {
      const [from, to] = dateRange.split(',');
      if (from?.trim()) params.from = from.trim();
      if (to?.trim()) params.to = to.trim();
    }
    return params;
  }, [currentPage, perPage, dateRange]);

  const webhookParams = useMemo(() => {
    const params: Record<string, unknown> = {
      page: currentPage,
      per_page: perPage
    };
    const status = statusFilter?.trim();
    if (status) params['filter[status_eq]'] = status;
    return params;
  }, [currentPage, perPage, statusFilter]);

  const { data: webhooksData, isLoading: loadingWebhooks } = useGetWebhooks(webhookParams, {
    enabled: tab === 0
  } as any);

  const { data: logsData, isLoading: loadingLogs } = useGetWebhookLogs(logParams, { enabled: tab === 1 } as any);

  const deleteWebhookMutation = useDeleteWebhook();

  const webhookColumns = useMemo(
    () =>
      getWebhookColumns({
        onEdit: (id) => navigate(`${WEBHOOKS_BASE}/${id}`),
        onDelete: (id) => deleteWebhookMutation.mutate(id),
        isDeleting: deleteWebhookMutation.isPending
      }),
    [deleteWebhookMutation, navigate]
  );

  const logColumns = useMemo(
    () =>
      getWebhookLogColumns({
        onView: (id) => navigate(`${WEBHOOKS_BASE}/logs/${id}`)
      }),
    [navigate]
  );

  return (
    <Box>
      <Box mb={2.5}>
        <Typography variant='h6' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: 20 } }}>
          Webhooks
        </Typography>
        <Typography sx={{ fontSize: { xs: '14px', lg: 16 } }} mt={0.5} color='secondary'>
          Send real-time events from {agencyOrganization?.name} to your endpoints.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, value) => {
          setTab(value);
          setPage(1);
        }}
        sx={{
          width: '100%',
          mb: 2,
          backgroundColor: '#fff',
          borderRadius: '4px',
          padding: 0.5,
          border: '1px solid #F0F0F0',
          minHeight: 44,
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTabs-scroller': { width: '100%' },
          '& .MuiTabs-flexContainer': { width: '100%' },
          '& .MuiTab-root': {
            minHeight: 44,
            flex: 1,
            maxWidth: 'none',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 14,
            color: 'text.secondary',
            borderRadius: '4px'
          },
          '& .Mui-selected': {
            backgroundColor: 'primary.light',
            color: 'text.primary',
            fontWeight: 500
          }
        }}
      >
        <Tab label='Webhooks' />
        <Tab label='Logs' />
      </Tabs>

      {tab === 0 && (
        <>
          <Box
            display='flex'
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent='space-between'
            alignItems={{ xs: 'stretch', sm: 'center' }}
            gap={1.5}
            mb={2}
          >
            <Typography sx={sectionHeaderSx}>Webhooks</Typography>
            <Box
              display='flex'
              flexDirection={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              gap={1.5}
              width={{ xs: '100%', sm: 'auto' }}
            >
              <Box minWidth={{ xs: '100%', sm: 180 }} width={{ xs: '100%', sm: 180 }}>
                <Select
                  name='status'
                  label='Status'
                  control={webhooksFilterControl}
                  sortOrder='none'
                  options={STATUS_FILTER_OPTIONS}
                  size='small'
                  sx={{ background: '#fff', maxHeight: '40px', width: '100%' }}
                />
              </Box>
              <Button
                startIcon={<MdAdd />}
                variant='contained'
                onClick={() => navigate(`${WEBHOOKS_BASE}/add`)}
                sx={{ width: { xs: '100%', sm: 'auto' }, alignSelf: { sm: 'center' } }}
              >
                Create Webhook
              </Button>
            </Box>
          </Box>

          <DataTable
            data={webhooksData?.data || []}
            columns={webhookColumns}
            isLoading={loadingWebhooks}
            sorting={sorting}
            onSortingChange={setSorting}
          />
          <Pagination
            totalPages={webhooksData?.meta?.total_pages}
            totalCount={webhooksData?.meta?.total_count ?? 0}
            currentPage={currentPage}
            perPage={perPage}
            onChange={setPage}
            onPerPageChange={setPerPage}
          />
        </>
      )}

      {tab === 1 && (
        <>
          <Box
            display='flex'
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent='space-between'
            alignItems={{ xs: 'stretch', sm: 'center' }}
            gap={1.5}
            mb={2}
          >
            <Typography sx={sectionHeaderSx}>Logs</Typography>
            <Box minWidth={{ xs: '100%', sm: 270 }} width={{ xs: '100%', sm: 270 }}>
              <DateRangePicker name='dateRange' control={control} createdAt={agencyOrganization?.created_at} />
            </Box>
          </Box>

          <DataTable
            data={logsData?.data || []}
            columns={logColumns}
            isLoading={loadingLogs}
            sorting={sorting}
            onSortingChange={setSorting}
          />
          <Pagination
            totalPages={logsData?.meta?.total_pages}
            totalCount={logsData?.meta?.total_count ?? 0}
            currentPage={currentPage}
            perPage={perPage}
            onChange={setPage}
            onPerPageChange={setPerPage}
          />
        </>
      )}

      <Outlet />
    </Box>
  );
}
